/**
 * Cardo — Client-Side Risk Scoring Engine
 *
 * Mirrors the backend scoring logic in risk_scoring.py.
 * Used for instant UI updates when profile fields change.
 * The backend remains the source of truth for saved scores.
 */

import type {
  SiteProfile,
  WeatherData,
  ScoreResult,
  Recommendation,
} from "./types";

const PHASE_RISK: Record<string, number> = {
  sitework_excavation: 85,
  steel_erection: 90,
  roofing: 95,
  concrete_formwork: 80,
  demolition: 88,
  mechanical_electrical: 70,
  exterior_envelope: 75,
  interior_finishes: 35,
  commissioning: 45,
  mobilization: 25,
};

const HIGH_FALL = [
  "steel_erection",
  "roofing",
  "exterior_envelope",
  "concrete_formwork",
];

const WEIGHTS: Record<string, number> = {
  schedule_phase: 0.25,
  weather_environmental: 0.2,
  compliance: 0.2,
  workforce: 0.15,
  historical: 0.1,
  equipment: 0.05,
  temporal: 0.05,
};

function cap(n: number): number {
  return Math.min(100, Math.round(n * 10) / 10);
}

function rec(
  p: number,
  cat: string,
  msg: string,
  act: string,
): Recommendation {
  return { priority: p, category: cat, message: msg, action: act };
}

function scoreSchedule(profile: SiteProfile): number {
  const phases = profile.schedule_phases;
  if (phases.length === 0) return 0;
  let base = Math.max(...phases.map((p) => PHASE_RISK[p] ?? 50));
  if (profile.concurrent >= 3) base *= 1.25;
  else if (profile.concurrent >= 2) base *= 1.15;
  base *= 1 + profile.pressure * 0.2;
  return cap(base);
}

function scoreWeather(wx: WeatherData): [number, Recommendation[]] {
  let s = 0;
  const r: Recommendation[] = [];
  const t = wx.temperature_f;

  if (t >= 105) {
    s += 50;
    r.push(
      rec(
        1,
        "Heat Stress",
        `Extreme heat: ${t}°F`,
        "Mandatory 15-min rest every 2 hours. Water within 50ft.",
      ),
    );
  } else if (t >= 90) {
    s += 35;
    r.push(
      rec(
        2,
        "Heat Stress",
        `High heat: ${t}°F`,
        "15-min rest breaks every 2 hours. Monitor for heat illness.",
      ),
    );
  } else if (t >= 80) s += 20;

  if (t <= 20) {
    s += 30;
    r.push(
      rec(
        2,
        "Cold Stress",
        `Extreme cold: ${t}°F`,
        "Warming breaks. Monitor for frostbite/hypothermia.",
      ),
    );
  } else if (t <= 40) s += 15;

  const w = wx.wind_gust_mph ?? wx.wind_speed_mph;
  if (w >= 40) {
    s += 35;
    r.push(
      rec(
        1,
        "High Wind",
        `Dangerous: ${w} mph`,
        "STOP crane ops and elevated work.",
      ),
    );
  } else if (w >= 25) {
    s += 20;
    r.push(
      rec(
        2,
        "High Wind",
        `Advisory: ${w} mph`,
        "Evaluate crane ops. Secure loose materials.",
      ),
    );
  }

  if (wx.precipitation_probability >= 70) {
    s += 15;
    r.push(
      rec(
        3,
        "Precipitation",
        `${wx.precipitation_probability}% chance`,
        "Wet conditions prep. Slip/fall review.",
      ),
    );
  } else if (wx.precipitation_probability >= 40) s += 8;

  if (wx.visibility_miles < 0.25) s += 30;
  else if (wx.visibility_miles < 1) s += 15;

  if (wx.lightning_risk) {
    s += 25;
    r.push(
      rec(
        1,
        "Lightning",
        "Detected within 10 miles",
        "EVACUATE elevated positions. 30-30 rule.",
      ),
    );
  }

  return [Math.min(100, s), r];
}

function scoreWorkforce(profile: SiteProfile): [number, Recommendation[]] {
  let s = 0;
  const r: Recommendation[] = [];
  const wf = profile.workforce;

  if (wf.total_workers >= 200) s += 25;
  else if (wf.total_workers >= 100) s += 15;
  else if (wf.total_workers >= 50) s += 10;

  if (wf.total_workers > 0) {
    const pct = wf.new_workers_under_90_days / wf.total_workers;
    if (pct >= 0.3) {
      s += 30;
      r.push(
        rec(
          2,
          "New Workers",
          `${wf.new_workers_under_90_days} new (${Math.round(pct * 100)}%)`,
          "Assign mentors. Enhanced orientation.",
        ),
      );
    } else if (pct >= 0.15) {
      s += 15;
      r.push(
        rec(
          3,
          "New Workers",
          `${wf.new_workers_under_90_days} new workers`,
          "Pair with experienced workers.",
        ),
      );
    }
  }

  if (wf.trades_on_site >= 8) {
    s += 20;
    r.push(
      rec(
        3,
        "Trade Coordination",
        `${wf.trades_on_site} trades`,
        "Pre-task planning with all foremen.",
      ),
    );
  } else if (wf.trades_on_site >= 5) s += 10;

  if (wf.overtime_hours_this_week >= 15) {
    s += 25;
    r.push(
      rec(
        2,
        "Fatigue",
        `${wf.overtime_hours_this_week}h OT`,
        "Evaluate additional crews.",
      ),
    );
  } else if (wf.overtime_hours_this_week >= 8) s += 12;

  if (wf.night_shift) {
    s += 15;
    r.push(rec(3, "Night Shift", "Active", "Verify lighting. High-vis PPE."));
  }

  return [Math.min(100, s), r];
}

function scoreCompliance(profile: SiteProfile): [number, Recommendation[]] {
  let s = 0;
  const r: Recommendation[] = [];
  const co = profile.compliance;

  if (co.open_violations >= 5) {
    s += 35;
    r.push(
      rec(
        1,
        "Violations",
        `${co.open_violations} open`,
        "Assign each with deadline. Escalate if >48h.",
      ),
    );
  } else if (co.open_violations >= 2) s += 20;

  if (co.violations_last_7_days >= 3) {
    s += 25;
    r.push(
      rec(
        1,
        "Violation Trend",
        `${co.violations_last_7_days} in 7 days`,
        "Safety stand-down for affected trades.",
      ),
    );
  } else if (co.violations_last_7_days >= 1) s += 10;

  if (co.ppe_compliance_rate < 80) {
    s += 30;
    r.push(
      rec(1, "PPE", `${co.ppe_compliance_rate}%`, "Immediate enforcement."),
    );
  } else if (co.ppe_compliance_rate < 90) s += 15;

  if (co.days_since_last_incident <= 7) {
    s += 20;
    r.push(
      rec(
        2,
        "Recent Incident",
        `${co.days_since_last_incident} days ago`,
        "Verify corrective actions. Brief all crews.",
      ),
    );
  } else if (co.days_since_last_incident <= 30) s += 10;

  if (!co.toolbox_talk_completed_today) {
    s += 10;
    r.push(
      rec(3, "Toolbox Talk", "Not completed", "Complete before work begins."),
    );
  }

  return [Math.min(100, s), r];
}

function scoreEquipment(profile: SiteProfile): [number, Recommendation[]] {
  let s = 0;
  const r: Recommendation[] = [];
  const eq = profile.equipment;

  if (eq.cranes_active >= 2) s += 40;
  else if (eq.cranes_active === 1) s += 25;

  if (eq.critical_lifts_planned) {
    s += 30;
    r.push(
      rec(
        1,
        "Critical Lift",
        "Planned today",
        "Verify plan. Exclusion zone. Brief all trades.",
      ),
    );
  }

  if (eq.heavy_equipment_count >= 5) s += 20;
  else if (eq.heavy_equipment_count >= 2) s += 10;

  if (eq.man_lifts_active >= 3) s += 15;
  else if (eq.man_lifts_active >= 1) s += 8;

  return [Math.min(100, s), r];
}

function scoreTemporal(): number {
  const h = new Date().getHours();
  const dow = new Date().getDay();
  const mo = new Date().getMonth() + 1;
  let s = 0;

  if (h >= 14 && h <= 16) s += 25;
  else if (h >= 12) s += 15;
  else if (h < 7 || h > 18) s += 20;

  if (dow === 1) s += 15;
  else if (dow === 5) s += 10;

  if ([6, 7, 8].includes(mo)) s += 15;
  else if ([12, 1, 2].includes(mo)) s += 10;

  return Math.min(100, s);
}

function detectSIF(profile: SiteProfile, wx: WeatherData): string[] {
  const flags: string[] = [];
  const phases = profile.schedule_phases;
  const eq = profile.equipment;
  const wf = profile.workforce;
  const wind = wx.wind_gust_mph ?? wx.wind_speed_mph;

  if (phases.some((p) => HIGH_FALL.includes(p)))
    flags.push("SIF-P: Fall from height — active work at elevation");
  if (eq.cranes_active > 0)
    flags.push("SIF-P: Crane operations — suspended load exposure");
  if (phases.includes("sitework_excavation"))
    flags.push("SIF-P: Excavation/trench — cave-in exposure");
  if (phases.includes("mechanical_electrical"))
    flags.push("SIF-P: Electrical contact — energized systems");
  if (eq.heavy_equipment_count >= 2 && wf.total_workers >= 20)
    flags.push("SIF-P: Struck-by — heavy equipment near workers");
  if (eq.critical_lifts_planned)
    flags.push("SIF-P: Critical lift — maximum energy release potential");
  if (wind >= 30 && eq.cranes_active > 0)
    flags.push("SIF-P CRITICAL: High wind + crane ops — evaluate stop-work");
  if (
    wx.lightning_risk &&
    phases.some((p) => HIGH_FALL.includes(p))
  )
    flags.push(
      "SIF-P CRITICAL: Lightning + elevated work — immediate evacuation",
    );

  return flags;
}

export function computeScore(
  profile: SiteProfile,
  wx: WeatherData,
): ScoreResult {
  const schedScore = scoreSchedule(profile);
  const [weatherScore, weatherRecs] = scoreWeather(wx);
  const [workforceScore, workforceRecs] = scoreWorkforce(profile);
  const [complianceScore, complianceRecs] = scoreCompliance(profile);
  const [equipmentScore, equipmentRecs] = scoreEquipment(profile);
  const temporalScore = scoreTemporal();

  const co = profile.compliance;
  let historicalScore = 0;
  if (co.days_since_last_incident <= 30) historicalScore = 60;
  else if (co.days_since_last_incident <= 90) historicalScore = 30;

  const scores: Record<string, number> = {
    schedule_phase: schedScore,
    weather_environmental: weatherScore,
    compliance: complianceScore,
    workforce: workforceScore,
    historical: historicalScore,
    equipment: equipmentScore,
    temporal: temporalScore,
  };

  let overall = Object.keys(scores).reduce(
    (sum, k) => sum + scores[k] * (WEIGHTS[k] ?? 0),
    0,
  );

  const sif = detectSIF(profile, wx);
  if (sif.some((f) => f.includes("CRITICAL"))) overall = Math.max(overall, 85);
  else if (sif.length > 0) overall = Math.max(overall, 60);
  overall = cap(overall);

  const level =
    overall >= 76
      ? "critical"
      : overall >= 51
        ? "high"
        : overall >= 26
          ? "moderate"
          : "low";

  const recs = [
    ...weatherRecs,
    ...workforceRecs,
    ...complianceRecs,
    ...equipmentRecs,
  ].sort((a, b) => a.priority - b.priority);

  return { overall, level, scores, recs, sif };
}
